import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Create the AuthContext
const AuthContext = createContext(null);

// AuthProvider component that wraps the app
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const sessionId = localStorage.getItem('adminSessionId');
    const userData = localStorage.getItem('adminUser');
    
    if (sessionId && userData) {
      try {
        setIsAuthenticated(true);
        setUser(JSON.parse(userData));
      } catch (err) {
        // Invalid stored data, clear it
        localStorage.removeItem('adminSessionId');
        localStorage.removeItem('adminUser');
      }
    }
    
    setLoading(false);
  }, []); // Only run once on mount

  // Listen for localStorage changes (when other components clear auth)
  useEffect(() => {
    const handleStorageChange = () => {
      const sessionId = localStorage.getItem('adminSessionId');
      const userData = localStorage.getItem('adminUser');
      
      if (!sessionId || !userData) {
        setIsAuthenticated(false);
        setUser(null);
      }
    };

    // Listen for storage events (cross-tab) and custom events (same-tab)
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authCleared', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authCleared', handleStorageChange);
    };
  }, []);

  // Login function
  const login = useCallback(async (username, password) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Store session and user data
        localStorage.setItem('adminSessionId', result.sessionId);
        localStorage.setItem('adminUser', JSON.stringify(result.user));
        
        // Update state
        setIsAuthenticated(true);
        setUser(result.user);
        
        return { success: true };
      } else {
        const errorMessage = result.error || 'Login failed';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (err) {
      const errorMessage = err.message || 'Network error during login';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      const sessionId = localStorage.getItem('adminSessionId');
      if (sessionId) {
        // Call logout endpoint (don't wait for response)
        fetch(`${API_BASE_URL}/api/admin/logout`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Id': sessionId,
          },
        }).catch(() => {
          // Ignore errors - we're logging out anyway
        });
      }
    } finally {
      // Always clear local state
      localStorage.removeItem('adminSessionId');
      localStorage.removeItem('adminUser');
      setIsAuthenticated(false);
      setUser(null);
      setError(null);
      
    }
  }, []);

  // Clear authentication (for use by other hooks when session expires)
  const clearAuth = useCallback(() => {
    localStorage.removeItem('adminSessionId');
    localStorage.removeItem('adminUser');
    setIsAuthenticated(false);
    setUser(null);
    setError(null);
    
    // Dispatch custom event to notify any remaining listeners
    window.dispatchEvent(new Event('authCleared'));
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);


  const value = {
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    clearAuth,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;